import React, { useState } from 'react';
import {
  Box,
  Button,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import {
  CandidateDetailsAction,
  CandidateStatus,
  hasPermission,
} from '@constants';
import { useAppSelector } from '@redux/hooks';
import { selectPermissions } from '@redux/accounts/accounts.selectors';
import { ICandidateDetails } from '@types';
import { isInterviewRoundOpen, normalizeCandidateStatus } from '@utils';
import { useOutlinedOnTouch } from '@hooks';
import { buttonSizeSx } from 'theme/buttons';

const OPEN_STATUSES = [
  CandidateStatus.Submitted,
  CandidateStatus.CallRequested,
  CandidateStatus.CallCancelled,
  CandidateStatus.CallScheduled,
  CandidateStatus.InterviewDone,
];

export const CandidateActionsMenu: React.FC<{
  data: ICandidateDetails;
  primaryActions?: CandidateDetailsAction[];
  onAction: (action: CandidateDetailsAction) => void;
}> = ({ data, primaryActions = [], onAction }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const moreButton = useOutlinedOnTouch();
  const canUpdateCandidate = hasPermission(
    useAppSelector(selectPermissions),
    'H:W',
  );

  const status = normalizeCandidateStatus(data.status);
  const latestRound = data.interview?.at(-1) || null;
  const roundIsOpen = isInterviewRoundOpen(latestRound);
  const isAwaitingReview = status === CandidateStatus.Submitted;
  const isInterviewHeld = status === CandidateStatus.InterviewDone;
  const isCancelled = status === CandidateStatus.CallCancelled;
  const isPipelineOpen = Boolean(status && OPEN_STATUSES.includes(status));

  const canChangeBooking = roundIsOpen && !isAwaitingReview && !isInterviewHeld;
  const bookingHint = canChangeBooking
    ? ''
    : latestRound && !roundIsOpen
      ? 'This interview has already been written up'
      : isInterviewHeld
        ? 'The interview has already been held'
        : 'No interview is booked yet';

  const hasOpenInterviewRound = roundIsOpen && isInterviewHeld;

  const canDecide = isPipelineOpen && (isAwaitingReview || roundIsOpen);
  const hireAction = isAwaitingReview
    ? CandidateDetailsAction.APPROVE_CANDIDATE
    : CandidateDetailsAction.CLIENT_INTERVIEW_PASSED;
  const skipAction = isAwaitingReview
    ? CandidateDetailsAction.SKIP_CANDIDATE
    : CandidateDetailsAction.CLIENT_INTERVIEW_FAILED;
  const decisionHint = canDecide
    ? ''
    : 'This candidate has already been decided on';

  const select = (action: CandidateDetailsAction) => () => {
    setAnchor(null);
    onAction(action);
  };

  const items = [
    {
      label: 'Proceed to hiring',
      icon: <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />,
      action: hireAction,
      enabled: canUpdateCandidate && canDecide,
      hint: decisionHint,
    },
    {
      label: 'Ask a question',
      icon: <HelpOutlineIcon sx={{ fontSize: 16 }} />,
      action: CandidateDetailsAction.ASK_QUESTION,
      enabled: canUpdateCandidate,
      hint: '',
    },
    {
      label: 'Reschedule interview',
      icon: <EventRepeatOutlinedIcon sx={{ fontSize: 16 }} />,
      action: CandidateDetailsAction.RESCHEDULE_INTERVIEW,
      enabled: canUpdateCandidate && canChangeBooking,
      hint: bookingHint,
    },
    {
      label: 'Cancel interview',
      icon: <HighlightOffIcon sx={{ fontSize: 16 }} />,
      action: CandidateDetailsAction.CANCEL_INTERVIEW,
      enabled: canUpdateCandidate && canChangeBooking && !isCancelled,
      hint: isCancelled ? 'This interview is already cancelled' : bookingHint,
    },
    {
      label: 'Schedule next round',
      icon: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
      action: CandidateDetailsAction.SCHEDULE_NEXT_ROUND,
      enabled: canUpdateCandidate && hasOpenInterviewRound,
      hint: hasOpenInterviewRound
        ? ''
        : 'Available once an interview has been held and is awaiting your feedback',
    },
    {
      label: 'Skip candidate',
      icon: <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />,
      action: skipAction,
      enabled: canUpdateCandidate && canDecide,
      hint: decisionHint,
    },
  ].filter((item) => !primaryActions.includes(item.action));

  return (
    <>
      <Button
        variant={moreButton.variant}
        startIcon={<MoreHorizIcon sx={{ width: 16, height: 16 }} />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ ...buttonSizeSx.md, ...moreButton.sx }}
      >
        <Box component="span" sx={{ display: { sm: 'none' } }}>
          More
        </Box>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          Additional actions
        </Box>
      </Button>

      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              minWidth: 240,
              borderRadius: '4px',
              border: `1px solid ${theme.palette.background.divider}`,
              '& .MuiList-root': { py: 0.5 },
            }),
          },
        }}
      >
        {items.map((item) => (
          <Tooltip key={item.label} title={item.hint} placement="left">
            <span>
              <MenuItem
                disabled={!item.enabled}
                onClick={select(item.action)}
                sx={{ height: 40, gap: 1, px: 1.5, color: 'text.primary' }}
              >
                <ListItemIcon
                  sx={{ minWidth: 'auto', color: 'text.secondary' }}
                >
                  {item.icon}
                </ListItemIcon>
                {item.label}
              </MenuItem>
            </span>
          </Tooltip>
        ))}
      </Menu>
    </>
  );
};
